import { Slide, createTheme } from '@mui/material';
import {DEFAULT_AUTO_HIDE_DURATION, DEFAULT_TRANSITION_DURATION} from 'contexts/SnackbarContext';

// export const adminFontFamily = 'Gotham,Metropolis,Roboto,sans-serif';
export const adminFontFamily = 'Roboto,sans-serif';

export enum ADMIN_COLORS {
  // commons
  BLACK = '#000000', // textPrimary
  GREY = '#555', // textSecondary : admin menu item text and postcard back text
  MEDIUM_GREY = '#EBEBEB', // admin mode message viewer divider (why another divider color ?) and main mode disabled visio overlay
  LIGHTER_GREY = '#F3F3F3', // main mode partner card background
  WHITE = '#FFFFFF',
  WHITE_HOVERED = 'rgba(255, 255, 255, 0.86)',
  WHITE_DISABLED = 'rgba(255, 255, 255, 0.66)',
  WHITE_BORDER_HOVERED = 'rgba(255, 255, 255, 0.36)',

  // admin mode button blue & calendar texts
  BLUE = '#2254B0',
  // main mode calendar cell border
  TRANSPARENT_BLUE = 'rgba(34, 84, 176, 0.2)', 
  // background
  VERY_LIGHT_BLUE = '#F5FAFE',
  
  // primary
  DARKER_BLUE = '#152B50',
  DARK_BLUE = '#1E3863',
  
  // secondary / admin mode contacts
  LIGHT_BLUE = '#2980B9',

  // error / cancel / suppr / admin mode organizer
  DARK_RED = '#C0392B',

  // divider
  LIGHT_GREY = '#EEEEEE',
  
  // main mode primary : contact violet
  VIOLET = '#6C5CE7',
  LIGHT_VIOLET = '#F2F3FF',

  // main mode secondary : share green
  GREEN = '#00B894',
  LIGHT_GREEN = 'rgba(0, 184, 148, 0.5)',

  // main mode tertiary / error : organize orange / salmon
  RED = '#ED6A5A',
  LIGHT_RED = 'rgba(237, 106, 90, 0.5)',

  // main mode quaternary / warning : help yellow
  YELLOW = '#F2CD5D',
  LIGHT_YELLOW = 'rgba(242, 205, 93, 0.5)',

  MAIN = '#172B4D', // building page body1 and subtitle1 : so not such a main... and why not DARKER_BLUE ? / admin mode home
  SECONDARY = '#11CDEF', // unused

  DARK_GREEN = '#27AE60',
  ORANGE = '#E67E22', // audio player progess bar (coming from nowhere)
}

const theme = createTheme({
  breakpoints: {
    values: {
      xxs: 0,
      xs: 400,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1920,
      xxl: 2560,
    },
  },
  palette: {
    primary: {
      dark: ADMIN_COLORS.DARKER_BLUE,
      main: ADMIN_COLORS.DARK_BLUE,
      light: ADMIN_COLORS.BLUE
    },
    secondary: {
      main: ADMIN_COLORS.LIGHT_BLUE,
    },
    reversed: {
      main: ADMIN_COLORS.WHITE,
    },
    success: {
      main: ADMIN_COLORS.GREEN,
    },
    info: {
      main: ADMIN_COLORS.LIGHT_BLUE,
    },
    warning: {
      main: ADMIN_COLORS.YELLOW,
    },
    error: {
      main: ADMIN_COLORS.DARK_RED,
    },
    background: {
      default: ADMIN_COLORS.VERY_LIGHT_BLUE,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
      },
      variants: [
        {
          props: { variant: 'reversed-contained' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
            fontWeight: 500,
            fontSize: 18,
            padding: '2px 12px',
            '&:hover': {
              backgroundColor: ADMIN_COLORS.WHITE_HOVERED,
            },
            '&.Mui-disabled': {
              backgroundColor: ADMIN_COLORS.WHITE_HOVERED,
            },
            '&.MuiButton-sizeLarge': {
              padding: '10px 20px',
              fontSize: 20,
            },
          }),
        },
        {
          props: { variant: 'reversed-outlined' },
          style: ({ theme }) => ({
            padding: '5px 16px',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            border: `1px solid ${theme.palette.common.white}`,
            '&:hover': {
              borderColor: ADMIN_COLORS.WHITE_HOVERED,
            },
            '&.Mui-disabled': {
              borderColor: ADMIN_COLORS.WHITE_HOVERED,
            },
            '&.MuiButton-sizeLarge': {
              padding: '7px 16px',
            },
          }),
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontFamily: adminFontFamily,
          textTransform: 'none',
        },
        contained: ({ theme, ownerState: {color} }) => ({
          backgroundColor: theme.palette[color].main,
          '&:not(.MuiLoadingButton-root).Mui-disabled': {
            backgroundColor: ADMIN_COLORS.WHITE_HOVERED,
          },
        }),
        startIcon: {
          '&>*:nth-of-type(1)': {
            fontSize: 16
          }
        },
        sizeLarge: {
          fontSize: 20,
          fontWeight: 500,
          borderWidth: '3px !important',
          padding: '10px 20px',
          '&:has(svg)': {
            lineHeight: 1
          },
          '& svg': {
            fontSize: '32px !important',
          }
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontFamily: adminFontFamily,
          textTransform: 'none',
          backgroundColor: ADMIN_COLORS.VERY_LIGHT_BLUE,
          overflow: 'hidden',
          '&:before, &:after': {
            borderRadius: 10,
          }
        },
        input: {
          borderRadius: 10,
          padding: '15px 20px',
        }
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 2,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 16,
          }
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px'
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: {
          horizontal: 'center',
          vertical: 'top'
        },
        autoHideDuration: DEFAULT_AUTO_HIDE_DURATION,
        TransitionComponent: Slide,
        transitionDuration: DEFAULT_TRANSITION_DURATION
      },
      styleOverrides: {
        root: ({ theme }) => ({
          top: 0,
          [theme.breakpoints.up('sm')]: {
            top: 0,
          },
      
          '& .MuiAlert-root': {
            borderRadius: '0 0 16px 16px',
            padding: 10,
          },
      
          '& .MuiAlert-icon': {
            padding: '7px 5px',
          },
      
          '& .MuiAlert-message': {
            padding: '8px 20px',
          },
      
          '& .MuiAlert-action': {
            padding: '2px 7px 0 16px',
          },
        })
      }
    },
    MuiAlert: {
      defaultProps: {
        variant: 'filled',
        severity: 'error',
        elevation: 6
      },
    }
  },
  shadows: [
    'none',
    '0px 3px 3px -1px rgba(0,0,0,0.03),0px 3px 4px 0px rgba(0,0,0,0.08),0px 1px 8px 0px rgba(0,0,0,0.2)',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
  ]
});

const adminTheme = createTheme(theme, {
  typography: {
    fontFamily: adminFontFamily,
    h1: {
      color: ADMIN_COLORS.WHITE,
      fontSize: 30,
      fontWeight: 400,
    },
    h2: {
      color: ADMIN_COLORS.WHITE,
      fontSize: 30,
      fontWeight: 400,
      [theme.breakpoints.down('xs')]: {
        fontSize: 24,
      }
    },
    h3: {
      color: ADMIN_COLORS.WHITE,
      fontSize: 26,
      fontWeight: 400,
    },
    h5: {
      fontSize: 18,
    },
    h6: {
      fontWeight: 400,
      fontSize: '1.1rem',
      lineHeight: 1.1,
    },
    body1: {
      fontSize: 16,
    },
    body2: {
      fontSize: 14,
    },
    subtitle1: {
      fontSize: 20,
    },
    subtitle2: {
      fontSize: 40,
      fontWeight: 800,
    },
    button: {
      fontWeight: 400
    },
    placeholder: {
      fontStyle: 'italic',
    }
  }
});

export default adminTheme;